"use client";

import React, { useCallback, useEffect, useId, useRef } from "react";
import { Button, ButtonVariant, CloseIcon } from "../../atoms/button/button";
import { INERT_SLOT } from "../../atoms/inert-slot";
import { firePageView } from "../../services/analytics";
import {
  SURFACE_PRESETS,
  isSurfacePreset,
  resolveSurface,
  resolveSurfaceAttribute,
  type SurfacePreset
} from "../../tokens/surface-presets";
import styles from "./modal.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/** Modal — a native `<dialog>` opened with `showModal()`. Figma node ids and
 * the design disagreements are in `design-conflicts/modal.mdx`.
 *
 * ⚠ The bare `open` attribute renders a NON-modal dialog: no top layer, no
 * inert background, no `::backdrop`. `showModal()` supplies those, plus Tab
 * containment, Escape, and focus returned to the opener on close. */

/**
 * The glass steps `tokens/surface.css` publishes, re-exported from
 * `../../tokens/surface-presets` so the stories and the spec take their option
 * lists from one array. The modal's OWN steps below stay local — they name
 * rules in `modal.module.css`, not in the shared token layer.
 */
export { SURFACE_PRESETS, isSurfacePreset, resolveSurface, resolveSurfaceAttribute };
export type { SurfacePreset };

/**
 * The modal's OWN surface steps — the panel highlight, and the two halves of
 * the overlay. Three axes because they are three layers, and `published` on
 * each is the frame's own value. Closed unions, unlike `surface`: these name
 * selectors that exist in modal.module.css and nowhere else.
 */
export const MODAL_GRADIENTS = ["none", "soft", "published", "strong", "glow"] as const;
export const MODAL_SCRIMS = ["none", "subtle", "published", "strong", "dense"] as const;
export const MODAL_SCRIM_BLURS = ["none", "soft", "published", "heavy"] as const;

export type ModalGradient = (typeof MODAL_GRADIENTS)[number];
export type ModalScrim = (typeof MODAL_SCRIMS)[number];
export type ModalScrimBlur = (typeof MODAL_SCRIM_BLURS)[number];

/** Desktop widths beyond the published 900px panel. Closed for the same reason:
 * each names a selector in modal.module.css. */
export const MODAL_SIZES = ["extended"] as const;

export type ModalSize = (typeof MODAL_SIZES)[number];

/** Heading levels the title may render as. */
export const MODAL_TITLE_ELEMENTS = ["h1", "h2", "h3"] as const;

export type ModalTitleElement = (typeof MODAL_TITLE_ELEMENTS)[number];

/**
 * ⚠ Own-property lookup, not a bare index: `titleAs` arrives from a CMS
 * boundary, and a bare index answers `"constructor"` truthy — which reaches the
 * DOM as a tag name, where `"__proto__"` throws `InvalidCharacterError`.
 */
const TITLE_ELEMENTS: Record<ModalTitleElement, true> = {
  h1: true,
  h2: true,
  h3: true
};

function titleElement(value: string): ModalTitleElement {
  return Object.prototype.hasOwnProperty.call(TITLE_ELEMENTS, value) ?
    (value as ModalTitleElement) :
    "h2";
}

/** The dialog's accessible name: a visible `title` (announced through
 * `aria-labelledby`) or an `ariaLabel` where the design shows no heading. */
type ModalLabelProps =
  | {
      /** The heading, and the name the dialog is announced by. */
      title: string;
      /**
       * Which heading element the title renders as. `h2` suits a dialog
       * opened from a page that already has an `h1`. Pass `h1` where the
       * content carries its own `h1` sections, so the title is their peer
       * rather than being outranked by them.
       */
      titleAs?: ModalTitleElement;
      /** Ignored when `title` is set — the heading already names the dialog. */
      ariaLabel?: never;
    }
  | {
      title?: never;
      titleAs?: never;
      /** Required with no heading: the dialog's only accessible name. */
      ariaLabel: string;
    };

interface ModalBaseProps {
  /** Opens and closes the dialog; supplying it makes it CONTROLLED, and a
   * CHANGE drives the element rather than every render. ⚠ Escape and the close
   * button still close it and fire `onClose` — clear the state there, or the
   * prop and what is on screen diverge. */
  open?: boolean;
  /** Opens on first render and then leaves the dialog alone. Ignored when
   * `open` is supplied. */
  defaultOpen?: boolean;
  /** Fires after the dialog has closed, however it closed, and receives which
   * control closed it: one of `CLOSED_BY`, or `""` when nothing submitted. That
   * is the platform's own `returnValue`. */
  onClose?: (closedBy: string) => void;
  /** Read aloud for the close control, which has no wording of its own. */
  closeLabel?: string;
  /**
   * Replaces the ✕ glyph only. The close button's size, shape, behaviour and
   * accessible name are unchanged — the name comes from `closeLabel` through
   * `ariaLabel`, which outranks anything inside the button.
   *
   * ⚠ Pass a DECORATIVE node, unvalidated: anything focusable nests a control
   * inside a control, which no amount of labelling repairs. It is wrapped in an
   * `inert` span because `Button`'s icon box is aria-hidden but not inert — a
   * focusable node there would be a tab stop no screen reader can see.
   */
  closeIcon?: React.ReactNode;
  /**
   * Renders no ✕ at all — for a modal whose only exits are its own CTAs.
   *
   * ⚠ Not the opposite of `dismissible`, which decides only whether an overlay
   * click closes the dialog. Both off with no closing CTA leaves Escape as the
   * only exit, which a touch or pointer-only reader does not have.
   *
   * ⚠ It also moves where focus lands: `showModal()` focuses the first
   * focusable element, normally the ✕. Without it that becomes the primary CTA,
   * so mark the intended landing spot with `autofocus` on a child.
   */
  hideCloseControl?: boolean;
  /** Wording on the filled call to action. Omit it and no button renders. */
  primaryLabel?: string;
  /** Makes the primary CTA a link. Without one it closes the dialog. */
  primaryHref?: string;
  /** Names the primary CTA's click in reporting. Nothing fires without it. */
  primaryLinkName?: string;
  /** Wording on the light call to action. Omit it and no button renders. */
  secondaryLabel?: string;
  /** Makes the secondary CTA a link. Without one it closes the dialog. */
  secondaryHref?: string;
  /** Names the secondary CTA's click in reporting. Nothing fires without it. */
  secondaryLinkName?: string;
  /** The modal's own name in reporting — `"exit_offer"`. Unique per page, with
   * no default: it is the switch for the page view and for the CTAs' position
   * and destination, so without it nothing is reported. */
  modalName?: string;
  /** Where the page view reports the modal, in place of
   * `<page URL>/modal_<modalName>`. Omit it for that default. The CTAs keep
   * the default either way. */
  pageViewDestination?: string;
  /** The dialog's own id — what a declarative `<button commandfor command>`
   * points at, opening the modal with no JavaScript at the call site. Chrome
   * 135+ / Safari 18.4+ only; older engines ignore it, so `open` is the route
   * for a page that must work everywhere. */
  id?: string;
  /** Lets a click on the overlay outside the panel close the modal. Off by
   * default; Escape closes the dialog either way. */
  dismissible?: boolean;
  /**
   * The panel's glass step, from `tokens/surface.css` — `"glass-surface"`,
   * `"opaque"`, and the four in between. Omit it and nothing changes: the
   * panel's `var()` fallbacks paint the frame's own fill and blur. Any string
   * is accepted; an unrecognised one warns in development.
   *
   * ⚠ `"glass-surface"` is the panel's own step and still not a no-op — the
   * token fills at 56% where the frame fills at 60%. `"glass-2"` / `"glass-3"`
   * are fill-only; `"glass-none"` and `"opaque"` remove the blur too.
   */
  surface?: SurfacePreset | (string & {});
  /**
   * Which highlight the panel carries. Omit it for the published
   * `fill-highlight-top` sheen; `"none"` removes it, `"soft"` / `"strong"`
   * are half and double it.
   *
   * ⚠ `"glow"` is a different SHAPE, not another step: a narrow vertical
   * shaft down the panel, and it replaces the sheen rather than joining it.
   * The legal modal's frame paints that and no sheen.
   */
  gradient?: ModalGradient;
  /**
   * How dark the overlay behind the panel is. Omit it for the published 24%.
   * `"none"` makes the overlay clear — the page stays blurred and inert, so it
   * is a look rather than a behaviour.
   */
  scrim?: ModalScrim;
  /**
   * How hard the overlay blurs the page behind it. Omit it for the published
   * 20px. ⚠ `"none"` removes the blur entirely rather than setting it to zero —
   * one fewer compositing layer, not one more that does nothing.
   */
  scrimBlur?: ModalScrimBlur;
  /**
   * How wide the panel grows from 1280px up. Omit it for the published 900px;
   * `"extended"` is 1240px, for content laid out across a full-width frame. The
   * tablet panel and the phone sheet are the same either way.
   */
  size?: ModalSize;
  /** The body — text, or whatever the design drops into the content slot. */
  children?: React.ReactNode;
  className?: string;
}

export type ModalProps = ModalBaseProps & ModalLabelProps;

/** What `onClose` is handed, so a host compares against a constant. `ESCAPED`
 * is the empty string because that is what the platform leaves in
 * `returnValue` when nothing submitted. */
export const CLOSED_BY = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  CLOSE_CONTROL: "close",
  ESCAPED: ""
} as const;

/** Which `CLOSED_BY` each CTA reports. Derived, so renaming a `ButtonVariant`
 * collapses this to `never` rather than sending `onClose` an unmatched value. */
export type ModalCtaVariant = Extract<ButtonVariant, "primary" | "secondary">;

type ClosedByValue = (typeof CLOSED_BY)[keyof typeof CLOSED_BY];

const CTA_CLOSED_BY: Record<ModalCtaVariant, ClosedByValue> = {
  primary: CLOSED_BY.PRIMARY,
  secondary: CLOSED_BY.SECONDARY
};

/** How many modals are open, so nesting cannot unlock the page while one of
 * them is still open. */
let openModalCount = 0;
let restoreOverflow: string | null = null;
let restorePadInline: string | null = null;

/** Stops the page behind the dialog scrolling. ⚠ Inertness does not do this:
 * `showModal()` blocks clicks, focus and selection on the background but NOT
 * wheel or touch scrolling of the root. Released on close rather than on
 * `transitionend`, which never fires under `prefers-reduced-motion` and would
 * lock the page for good. */
function lockPageScroll(): void {
  openModalCount += 1;
  if (openModalCount > 1) {
    return;
  }
  const root = document.documentElement;
  restoreOverflow = root.style.overflow;
  restorePadInline = root.style.paddingInlineEnd;

  /* Measured BEFORE the overflow is touched — after it the scrollbar is gone
     and the gap reads 0. Logical, so it stays on the scrollbar's edge in RTL. */
  const gutter = window.innerWidth - root.clientWidth;
  if (gutter > 0) {
    root.style.paddingInlineEnd = `${gutter}px`;
  }
  root.style.overflow = "hidden";
}

function unlockPageScroll(): void {
  openModalCount = Math.max(0, openModalCount - 1);
  if (openModalCount > 0) {
    return;
  }
  document.documentElement.style.overflow = restoreOverflow ?? "";
  document.documentElement.style.paddingInlineEnd = restorePadInline ?? "";
  restoreOverflow = null;
  restorePadInline = null;
}

/** The modal's virtual path, shared by the page view and the CTAs'
 * destination. Absolute with the trailing slash collapsed — a bare pathname
 * reports `//modal_<name>` on the homepage. */
function modalVirtualPath(modalName?: string): string | undefined {
  if (!modalName || typeof window === "undefined") {
    return undefined;
  }

  const { origin, pathname } = window.location;
  return `${origin}${pathname.replace(/\/+$/, "")}/modal_${modalName}`;
}

/** The position dimension, which is the modal's name and nothing else. */
function modalLinkPositionFor(modalName?: string): string | undefined {
  return modalName ? `modal_${modalName}` : undefined;
}

/** Reports the modal as a page view. Only ever called from an effect, so
 * `location` is read on the client at fire time. */
function reportOpen(modalName: string, destination?: string): void {
  firePageView({
    linkPosition: modalLinkPositionFor(modalName),
    linkDestination: destination || modalVirtualPath(modalName)
  });
}

/**
 * One warning per unrecognised surface or step name. The closed unions are
 * checked here too because a CMS field reaches this past the compiler, and the
 * failure is silent: an unknown name matches no selector, so the frame's own
 * surface renders and the control merely looks broken. `undefined` passes — it
 * is how a prop is omitted.
 */
function warnUnknownStep(
  prop: string,
  value: string | undefined,
  allowed: readonly string[]
): void {
  if (value === undefined || allowed.includes(value)) {
    return;
  }

  console.warn(
    `[Modal] \`${prop}\` was "${value}", which is not one of ` +
      `${allowed.join(", ")}. No rule matches it, so the panel renders its own ` +
      "Figma surface and the control will look as though it does nothing. Omit " +
      "the prop for that default deliberately."
  );
}

/**
 * Development-only diagnostics, compiled out of production builds. Each case
 * below is legal to the compiler and invisible in the render — a `linkName`
 * with no `modalName` still reports, just from the body position and with no
 * page view at all. One object rather than a positional list because the four
 * adjacent `string | undefined` axes would transpose silently.
 */
type ModalWarningInput = {
  modalName?: string;
  linkNames: (string | undefined)[];
  title?: string;
  ariaLabel?: string;
  closeLabel?: string;
  open?: boolean;
  onClose?: (returnValue: string) => void;
  surface?: string;
  gradient?: string;
  scrim?: string;
  scrimBlur?: string;
  size?: string;
  hideCloseControl: boolean;
};

/* ⚠ THE INESCAPABLE MODAL — the one check here that runs in production too.
   ✕ suppressed, light dismiss off and no CLOSING CTA leaves the keyboard as
   the only exit, which is a WCAG 2.1.2 trap for a touch or pointer-only
   reader. A CTA with an `href` does not count: it navigates instead of
   closing, so it is not an in-page dismissal. */
type InescapableInput = {
  hideCloseControl: boolean;
  dismissible: boolean;
  hasDismissCta: boolean;
};

/* Returns whether it reported, so the caller latches the MESSAGE and not the
   check: a CMS value that arrives a render late still gets one. */
function warnIfInescapable({
  hideCloseControl,
  dismissible,
  hasDismissCta
}: InescapableInput): boolean {
  if (hideCloseControl && !dismissible && !hasDismissCta) {
    console.error(
      "[Modal] `hideCloseControl` is set with `dismissible` off and no closing " +
        "CTA, so Escape is the only way out — which a touch or pointer-only " +
        "reader does not have. Give it a CTA without an href, set " +
        "`dismissible`, or keep the close control."
    );
    return true;
  }

  return false;
}

function warnInDevelopment({
  modalName,
  linkNames,
  title,
  ariaLabel,
  closeLabel,
  open,
  onClose,
  surface,
  gradient,
  scrim,
  scrimBlur,
  size,
  hideCloseControl
}: ModalWarningInput): void {
  /* ⚠ `surface` alone is normalised, because its attribute is read by ABSENCE:
     `tokens/surface.css` resets nested surfaces with `:not([data-dds-surface])`,
     so a blank value — a PRESENT `data-dds-surface=""` — dodges the reset and
     inherits an ancestor's glass. The modal's own three are inert when blank. */
  warnUnknownStep("surface", resolveSurface(surface), SURFACE_PRESETS);
  warnUnknownStep("gradient", gradient, MODAL_GRADIENTS);
  warnUnknownStep("scrim", scrim, MODAL_SCRIMS);
  warnUnknownStep("scrimBlur", scrimBlur, MODAL_SCRIM_BLURS);

  if (size !== undefined && !(MODAL_SIZES as readonly string[]).includes(size)) {
    console.warn(
      `[Modal] \`size\` was "${size}", which is not one of ${MODAL_SIZES.join(", ")}. ` +
        "No rule matches it, so the panel keeps its published width. Omit the " +
        "prop for that default deliberately."
    );
  }

  /* Checked here because the compiler cannot: the props union splits on the
     label, not on `open`. */
  if (open !== undefined && typeof onClose !== "function") {
    console.error(
      "[Modal] `open` is controlled but no `onClose` was passed. A native close " +
        "(Escape, light dismiss, form submit) closes the element without a React " +
        "commit, so `open` stays true and the modal can never be reopened. Pass " +
        "`onClose` and clear the state in it."
    );
  }

  /* The union forbids supplying NEITHER label; `title=""` still gets through. */
  const name = title ?? ariaLabel;
  if (typeof name !== "string" || name.trim().length === 0) {
    console.error(
      "[Modal] the dialog has no accessible name — `title` or `ariaLabel` is " +
        "present but empty or blank. A screen reader will announce \"dialog\" and " +
        "nothing about what it wants. Pass real wording (a CMS field that " +
        "arrived empty is the usual cause)."
    );
  }

  /* ⚠ The same gap one control down: `closeLabel = ""` defeats the default and
     leaves the DISMISS control nameless behind an `aria-hidden` glyph. Gated on
     the control rendering — with `hideCloseControl` there is nothing to name. */
  if (
    !hideCloseControl &&
    typeof closeLabel === "string" &&
    closeLabel.trim().length === 0
  ) {
    console.error(
      "[Modal] `closeLabel` is empty, so the close control has no accessible " +
        "name — it is announced as just \"button\". Pass real wording, or omit " +
        "the prop to take the default (a CMS field that arrived empty is the " +
        "usual cause)."
    );
  }

  if (modalName || !linkNames.some(Boolean)) {
    return;
  }

  console.warn(
    "[Modal] a linkName was given without `modalName`, so the click will " +
      "report from the default body position instead of `modal_<name>`, and " +
      "no modal page view will fire at all. Set `modalName`."
  );
}

function ModalImpl({
  open,
  defaultOpen = false,
  onClose,
  title,
  titleAs = "h2",
  ariaLabel,
  closeLabel = "Close",
  closeIcon,
  hideCloseControl = false,
  primaryLabel,
  primaryHref,
  primaryLinkName,
  secondaryLabel,
  secondaryHref,
  secondaryLinkName,
  modalName,
  pageViewDestination,
  id,
  dismissible = false,
  surface,
  gradient,
  scrim,
  scrimBlur,
  size,
  children,
  className
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  /* One page view per opening; reset by the close listener below. */
  const reportedOpeningRef = useRef(false);
  /* The latest `modalName` for the observer below, which is created once: a
     declarative open causes no commit, so it would otherwise hold mount's. */
  const modalNameRef = useRef(modalName);
  const pageViewDestinationRef = useRef(pageViewDestination);
  const titleId = `dds-modal-title-${useId().replace(/:/g, "")}`;
  const TitleTag = titleElement(titleAs);
  const isControlled = open !== undefined;
  const classes = [styles.dialog, className].filter(Boolean).join(" ");
  /* The two differ on `href`: a labelled CTA renders either way (`hasCtas`),
     but one WITH an href navigates instead of closing, so only the
     label-without-href form counts as an in-page dismissal. */
  const hasCtas = Boolean(primaryLabel) || Boolean(secondaryLabel);
  const hasDismissCta =
    (Boolean(primaryLabel) && !primaryHref) ||
    (Boolean(secondaryLabel) && !secondaryHref);

  /* ⚠ Reading `location` during render is safe only because neither value
     reaches the DOM — both go to `Button`'s click handler. Do not put either
     in markup without moving this to an effect. */
  const modalLinkPosition = modalLinkPositionFor(modalName);
  const modalLinkDestination = modalVirtualPath(modalName);

  const reportedInescapableRef = useRef(false);
  /* Held in a ref for the same reason `modalNameRef` is: one of the two
     reporters below is an attribute observer, whose closure cannot see a later
     render's props. */
  const inescapableRef = useRef({
    hideCloseControl,
    dismissible,
    hasDismissCta
  });

  if (process.env.NODE_ENV !== "production") {
    warnInDevelopment({
      modalName,
      linkNames: [primaryLinkName, secondaryLinkName],
      title,
      ariaLabel,
      closeLabel,
      open,
      onClose,
      surface,
      gradient,
      scrim,
      scrimBlur,
      size,
      hideCloseControl
    });
  }

  /* Latched, so the effect below and the attribute observer cannot both report. */
  const reportOpeningIfNeeded = useCallback(() => {
    const name = modalNameRef.current;
    if (reportedOpeningRef.current || !name) {
      return;
    }
    reportedOpeningRef.current = true;
    reportOpen(name, pageViewDestinationRef.current);
  }, []);

  /* WAT-11877: outside the NODE_ENV guard, and latched in an effect so a
     re-render cannot repeat it and a server render never logs it. Off the
     open signal rather than a dependency list, which would make the ordinary
     `<Modal open={false} hideCloseControl primaryHref … />` log a production
     console.error for a dialog nobody has opened. */
  const reportInescapableIfNeeded = useCallback(() => {
    if (reportedInescapableRef.current) {
      return;
    }
    reportedInescapableRef.current = warnIfInescapable(inescapableRef.current);
  }, []);

  /* The close EVENT rather than a handler per control: Escape and the form
     submit close the dialog by routes no handler sees. React's own `onClose`
     for <dialog> is React 19 and this repo is on 18.3. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return undefined;
    }
    /* Attached whether or not there is an `onClose`: it also clears the
       page-view latch so the NEXT opening reports. */
    const handleClose = () => {
      reportedOpeningRef.current = false;
      onClose?.(dialog.returnValue);
    };
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [onClose]);

  /* ⚠ The `!dialog.open` guard is load-bearing: `showModal()` THROWS on an
     already-open dialog and React runs effects twice on mount in development.
     `close(ESCAPED)` rather than bare, or `returnValue` keeps the PREVIOUS
     opening's value and reports an abandon as a confirm. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !isControlled) {
      return;
    }
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close(CLOSED_BY.ESCAPED);
    }
  }, [open, isControlled]);

  /* Uncontrolled: open once, on mount, and never again — the empty dependency
     list is the point. `!dialog.open` guards the same throw as above. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (isControlled || !defaultOpen || !dialog || dialog.open) {
      return;
    }
    dialog.showModal();
  }, []);

  /* Scroll-lock and the declarative opening, both driven off the `open`
     ATTRIBUTE — the one signal every open route shares, including a
     `commandfor` open that React never sees. ⚠ Must stay AFTER the two open
     effects above: the initial `sync()` reads `dialog.open` synchronously on
     mount. The cleanup unlocks so an unmount cannot strand the page. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return undefined;
    }

    let locked = false;
    const sync = () => {
      if (dialog.open) {
        if (!locked) {
          locked = true;
          lockPageScroll();
        }
        reportOpeningIfNeeded();
        reportInescapableIfNeeded();
      } else if (locked) {
        locked = false;
        unlockPageScroll();
      }
    };

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(dialog, { attributeFilter: ["open"] });
    dialog.addEventListener("close", sync);

    return () => {
      observer.disconnect();
      dialog.removeEventListener("close", sync);
      if (locked) {
        locked = false;
        unlockPageScroll();
      }
    };
  }, [reportOpeningIfNeeded, reportInescapableIfNeeded]);

  /* The page view and the inescapable-modal report. No dependency array,
     deliberately: neither the name nor the three exit props are in either open
     effect's dependencies, so a value resolving a render after the dialog opened
     would never report. ⚠ Must stay LAST, after both openers. */
  useEffect(() => {
    /* Written BEFORE the early return: values resolving while the dialog is
       SHUT would otherwise leave the observer holding the mount ones for
       good. */
    modalNameRef.current = modalName;
    pageViewDestinationRef.current = pageViewDestination;
    inescapableRef.current = {
      hideCloseControl,
      dismissible,
      hasDismissCta
    };

    if (!dialogRef.current?.open) {
      return;
    }
    reportOpeningIfNeeded();
    reportInescapableIfNeeded();
  });

  /* LIGHT-DISMISS fallback for engines without `closedby="any"`. A click on the
   * overlay reports the DIALOG as its target — `::backdrop` has no node of its
   * own to hit — so `target === dialog` means "outside the panel".
   *
   * ⚠ That only holds because the panel has no padding: the dialog element IS
   * the panel (`padding: 0`). Give it padding and a click just inside the edge
   * starts closing the modal; put padding on an inner wrapper instead.
   *
   * ⚠ Both ENDS of the gesture must be outside, which is why there are three
   * handlers. A `click` is dispatched at the nearest common inclusive ancestor
   * of `mousedown` and `mouseup` — the dialog itself for any gesture with one
   * end on the overlay — so the click target alone reads a drag out of the body
   * copy, and a drag into it, as "outside". */
  const pressedOutsidePanelRef = useRef(false);

  const handleDialogMouseDown = (event: React.MouseEvent<HTMLDialogElement>) => {
    pressedOutsidePanelRef.current = event.target === dialogRef.current;
  };

  /* ANDed in, not assigned: an assignment would also ARM a gesture that began
     inside the panel and merely ended on the overlay. */
  const handleDialogMouseUp = (event: React.MouseEvent<HTMLDialogElement>) => {
    pressedOutsidePanelRef.current =
      pressedOutsidePanelRef.current && event.target === dialogRef.current;
  };

  const handleDialogClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    const outsideThroughout = pressedOutsidePanelRef.current;
    /* Cleared so a press that never produced a click cannot arm a later one. */
    pressedOutsidePanelRef.current = false;

    if (!dismissible || !outsideThroughout || event.target !== dialogRef.current) {
      return;
    }
    dialogRef.current?.close(CLOSED_BY.ESCAPED);
  };

  /* `linkDestination` overrides Button's `href` default so a click inside a
     modal reports the modal's virtual path. A CTA with somewhere to go is a
     link; one without is a submit inside `<form method="dialog">`, the
     platform's own way to close a dialog — it sets `returnValue` for free. */
  const renderCta = (
    label: string,
    variant: ModalCtaVariant,
    href?: string,
    linkName?: string
  ) => {
    const button = (
      <Button
        className={styles.action}
        href={href}
        linkDestination={modalLinkDestination}
        linkName={linkName}
        linkPosition={modalLinkPosition}
        size="medium"
        type={href ? "button" : "submit"}
        /* Sets returnValue when THIS button is the submitter. Unconditional
           because Button's <a> branch renders no `value` at all. */
        value={CTA_CLOSED_BY[variant]}
        variant={variant}
      >
        {label}
      </Button>
    );

    return href ? (
      button
    ) : (
      <form className={styles.dialogForm} key={variant} method="dialog">
        {button}
      </form>
    );
  };

  return (
    <dialog
      aria-label={title ? undefined : ariaLabel}
      aria-labelledby={title ? titleId : undefined}
      className={classes}
      /* Lower-cased because it is an HTML attribute React does not know about —
         `closedBy` would be dropped. */
      closedby={dismissible ? "any" : undefined}
      /* ⚠ `undefined` is the default on purpose: React omits the attribute, so
         the `var()` fallbacks in modal.module.css are what paint. Do NOT give
         any of these a default — that defines the token and stops the component
         reading its own Figma value. Attributes rather than inline style, so
         CMS text can never land inside `backdrop-filter`. `surface` alone is
         gated by `resolveSurfaceAttribute` because its attribute is read by
         absence; the other three are inert when unknown. */
      data-dds-modal-gradient={gradient}
      data-dds-modal-scrim={scrim}
      data-dds-modal-scrim-blur={scrimBlur}
      data-dds-modal-size={size}
      data-dds-surface={resolveSurfaceAttribute(surface)}
      id={id}
      onClick={handleDialogClick}
      onMouseDown={handleDialogMouseDown}
      onMouseUp={handleDialogMouseUp}
      ref={dialogRef}
    >
      {/* The form exists only to carry `method="dialog"` for this one button,
          so `hideCloseControl` takes the whole row with it. Truthiness rather
          than `??` on `closeIcon`: `closeIcon={show && <Custom />}` evaluates
          to `false`, which would render nothing where the glyph belongs. */}
      {hideCloseControl ? null : (
        <form className={styles.closeRow} method="dialog">
          {/* ⚠ ATTRIBUTES, NEVER A PUSH. The button submits this form, so
              GTM's FORM SUBMIT trigger fires a `link_click` for it on its own.
              Shipping `data-linkposition` fills in the event GTM already sends;
              a push here would be a second event for one close. That is why
              there is no `closeLinkName` — the prop could only ever
              double-count. */}
          <Button
            ariaLabel={closeLabel}
            linkAttributes
            linkDestination={modalLinkDestination}
            linkPosition={modalLinkPosition}
            size="small"
            startIcon={
              closeIcon ?
                <span className={styles.closeSlot} {...INERT_SLOT}>{closeIcon}</span> :
                <CloseIcon />
            }
            type="submit"
            value={CLOSED_BY.CLOSE_CONTROL}
            variant="tertiary"
          />
        </form>
      )}

      {/* Title and close sit outside the scroll region, as the frame draws. */}
      {title ? (
        <div className={styles.titleBlock}>
          <TitleTag className={styles.title} id={titleId}>
            {title}
          </TitleTag>
        </div>
      ) : null}

      <div className={styles.body}>
        {children}
        {hasCtas ? (
          <div className={styles.buttons}>
            {primaryLabel ?
              renderCta(primaryLabel, "primary", primaryHref, primaryLinkName) :
              null}
            {secondaryLabel ?
              renderCta(secondaryLabel, "secondary", secondaryHref, secondaryLinkName) :
              null}
          </div>
        ) : null}
      </div>
    </dialog>
  );
}
export const Modal = withSafeRender(ModalImpl, "Modal");

export default Modal;
